import { MouseEvent, RefObject, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import classNames from "classnames";

import {
  getDatasetTags,
  getTempExportCount,
  getExportRequestData,
  getAllSelectedFilters,
  getTemporaryAssignedTags,
  getTemporaryExportDataArray,
  getFilteredSimilarityClusters,
} from "redux/SingleDataset/selectors";
import {
  addAllForTempExport,
  setLastSelectedIndex,
  submitSelectedCartTags,
  saveTemporarySelection,
  setTemporaryExportData,
  submitRemoveTagsRequest,
  updateTemporaryExportData,
  clearSelectedMedia,
  addAllMediaIDsFromClusterPageToTemp,
} from "redux/SingleDataset/actions";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

import { ERROR_RED, PARROT_GREEN } from "helpers/constants/colors";
import { ERROR, SUCCESS } from "helpers/constants/toastify";
import NotesIcon from "assets/icons/NotesIcon";
import TagIcon from "assets/icons/tagIcon";

import AssignTagsToast from "views/components/Cart/AssignTagsToast";
import TagsDropDown from "views/components/Cart/TagsDropDown";

import styles from "./style.module.scss";

interface ActionButtonProps {
  title: string;
  children?: JSX.Element;
  icon?: JSX.Element | number;
  state?: "selected" | "ghost";
  btnRef?: RefObject<HTMLDivElement>;
  onClick?: () => void;
}

const ActionButton = ({
  icon,
  title,
  state,
  btnRef,
  children,
  onClick,
}: ActionButtonProps) => {
  const actionBtnClassnames = classNames(styles.actionBtn, {
    [styles.selected]: state === "selected",
    [styles.ghost]: state === "ghost",
    [styles.clickable]: !!onClick,
  });

  return (
    <div className={actionBtnClassnames} ref={btnRef} onClick={onClick}>
      {icon && <div className={styles.actionIcon}>{icon}</div>}
      <div className={styles.actionTitle}>{title}</div>
      {children && <div className={styles.positioner}>{children}</div>}
    </div>
  );
};

type ToastActionType = "assign" | "remove";

const FloatingActions = () => {
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);

  const tagDropdownBtnRef = useRef<HTMLDivElement>(null);
  const toastIdRef = useRef<any>(null);

  const queryParamsFilters = useAllQueryParamsFilters();
  const { datasetId, clusterId } = useParams();
  const dispatch = useDispatch();

  const exportData = useSelector(getTemporaryExportDataArray);
  const exportCount = useSelector(getTempExportCount);
  const originalTags = useSelector(getDatasetTags);
  const assignedTags = useSelector(getTemporaryAssignedTags);
  const clusters = useSelector(getFilteredSimilarityClusters);
  const exportRequestData = useSelector((state) =>
    getExportRequestData(state, queryParamsFilters, true)
  );
  const allSelectedFilters = useSelector((state) =>
    getAllSelectedFilters(state, queryParamsFilters)
  );

  const selectedTags = useMemo(
    () => assignedTags.filter((t) => t.selected),
    [assignedTags]
  );

  const toastStyle = { borderRadius: "10px", marginBottom: "10px" };

  const selectedClusterOnDataPage =
    clusterId && exportData.length > 0
      ? exportData.find(
          (singleData) =>
            singleData.cluster_id === clusterId &&
            singleData.exportType === "cluster"
        )
      : null;

  const handleAssignTagsClick = () => {
    setShowTagsDropdown(!showTagsDropdown);
  };

  const toggleShowTagsDropdown = (e: MouseEvent) => {
    e.stopPropagation();
    setShowTagsDropdown(!showTagsDropdown);
  };

  const successNotification = (type: ToastActionType) => {
    const dynamicDescription =
      type === "assign"
        ? `Tag successfully assigned to ${exportCount}  ${
            exportCount > 1 ? "items" : "item"
          } `
        : `Tag successfully de-assigned from ${exportCount} ${
            exportCount > 1 ? "items" : "item"
          }  `;
    toastIdRef.current = toast(
      <AssignTagsToast
        status={SUCCESS}
        heading={`Tag assignment`}
        description={dynamicDescription}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,

      {
        style: { ...toastStyle, border: `2px solid ${PARROT_GREEN}` },
        closeButton: false,
      }
    );
  };

  const errorNotification = (type: ToastActionType) => {
    const dynamicDescription =
      type === "assign"
        ? "Error occoured while tagging. Please try again"
        : "Error occoured while removing tags. Please try again";
    toastIdRef.current = toast(
      <AssignTagsToast
        status={ERROR}
        heading={`Error!`}
        description={dynamicDescription}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      {
        closeOnClick: false,
        closeButton: false,
        style: { ...toastStyle, border: `2px solid ${ERROR_RED}` },
      }
    );
  };

  const handleSaveSelection = () => {
    dispatch(saveTemporarySelection(datasetId!) as any);
  };

  const handleTagSubmission = async (tagID: number) => {
    try {
      const requestBody: any = { [tagID]: exportRequestData };
      await dispatch(
        submitSelectedCartTags(
          datasetId!,
          requestBody,
          queryParamsFilters,
          clusterId
        ) as any
      );
      successNotification("assign");
      dispatch(setTemporaryExportData(null));
    } catch (error) {
      toast.dismiss(toastIdRef.current);
      errorNotification("assign");
    }
  };

  const handleTagRemoval = async (tagID: number) => {
    try {
      let requestBody: any = { [tagID]: exportRequestData };
      await dispatch(
        submitRemoveTagsRequest(
          datasetId!,
          requestBody,
          queryParamsFilters,
          clusterId
        ) as any
      );
      successNotification("remove");
    } catch (error) {
      toast.dismiss(toastIdRef.current);
      errorNotification("remove");
    }
  };

  const handleSelectAll = () => {
    if (!!clusterId) {
      if (!!selectedClusterOnDataPage) {
        //will remove the selected cluster from cart and add medias
        dispatch(
          updateTemporaryExportData(
            {
              ...selectedClusterOnDataPage,
              cluster_filters: allSelectedFilters,
            },
            "cluster"
          ) as any
        );
      }
      dispatch(clearSelectedMedia() as any);
      dispatch(
        addAllMediaIDsFromClusterPageToTemp(
          datasetId!,
          allSelectedFilters
        ) as any
      );
    } else if (!!datasetId) {
      dispatch(addAllForTempExport(clusters, allSelectedFilters) as any);
    }
  };
  const handleClearAll = () => {
    if (!!clusterId) {
      if (!!selectedClusterOnDataPage) {
        dispatch(
          updateTemporaryExportData(
            { ...selectedClusterOnDataPage, cluster_filters: {} },
            "cluster"
          ) as any
        );
      } else {
        dispatch(clearSelectedMedia() as any);
      }
    } else if (!!datasetId) {
      dispatch(setTemporaryExportData(null));
    }
    dispatch(setLastSelectedIndex(null));
  };

  return (
    <div className={styles.floatingActions}>
      <ActionButton
        state="selected"
        icon={exportCount}
        title="Selected Items"
      />

      <ActionButton
        title="Assign Tag"
        icon={<TagIcon />}
        btnRef={tagDropdownBtnRef}
        onClick={handleAssignTagsClick}
      >
        {showTagsDropdown ? (
          <TagsDropDown
            caller="floatingBar"
            allTags={originalTags}
            datasetID={datasetId!}
            btnRef={tagDropdownBtnRef}
            assignedTags={selectedTags}
            toggleShowTags={toggleShowTagsDropdown}
            onTagSubmission={handleTagSubmission}
            onTagRemoval={handleTagRemoval}
          />
        ) : (
          <noscript />
        )}
      </ActionButton>
      <div className={styles.divider}></div>
      <ActionButton
        title="Keep Selection"
        icon={<NotesIcon />}
        onClick={handleSaveSelection}
      />
      <ActionButton
        state="ghost"
        title="Select All"
        onClick={handleSelectAll}
      />
      <div className={styles.divider}></div>
      <ActionButton
        state="ghost"
        title="Clear Selection"
        onClick={handleClearAll}
      />
    </div>
  );
};
export default FloatingActions;
